import { useState } from "react";
import { FileDown, Plus } from "lucide-react";
import { api, type EventSetting, type PortalPayload } from "../../lib/api";
import { formatINRExact } from "../../lib/format";
import {
  ActionMessage,
  Button,
  Card,
  Checkbox,
  Field,
  Input,
  Modal,
  PanelSection,
  RowActions,
  useAdminAction,
} from "./shared";

interface Announcement {
  id: number;
  title: string;
  body: string;
  pinned: boolean;
  createdAt: string;
}

interface Faq {
  id: number;
  question: string;
  answer: string;
  sortOrder: number;
}

export default function ContentPanel({
  announcements,
  faqs,
  surplus,
  event,
  pricing,
  onSaved,
}: {
  announcements: Announcement[];
  faqs: Faq[];
  surplus: PortalPayload["surplus"];
  event: EventSetting;
  pricing: PortalPayload["pricing"];
  onSaved: () => void;
}) {
  // Data-completeness action items — derived from what is actually missing.
  const actionItems: Array<{ label: string; where: string }> = [];
  if (!event.venueName) actionItems.push({ label: "Venue / banquet-hall name", where: "Event tab → Venue name" });
  if (!event.venueFloor) actionItems.push({ label: "Venue floor", where: "Event tab → Venue floor" });
  if (!event.venueAddress) actionItems.push({ label: "Venue address", where: "Event tab → Venue address" });
  if (!event.scheduledAt) actionItems.push({ label: "Lunch date & event start time", where: "Event tab → Event date & time" });
  if (!event.lunchTime) actionItems.push({ label: "Lunch serving time", where: "Event tab → Lunch serving time" });
  if (event.venueStatus !== "confirmed") actionItems.push({ label: "Venue confirmation", where: "Event tab → Venue status" });
  if (pricing.perPersonExpensePaise <= 0) actionItems.push({ label: "Per-person expenditure figure", where: "Event tab → Per-person expenditure" });
  if (event.seniorContributionPaise <= 0) actionItems.push({ label: "Senior contribution amount", where: "Event tab → Senior contribution" });
  if (event.juniorContributionPaise <= 0) actionItems.push({ label: "Junior contribution amount", where: "Event tab → Junior contribution" });
  actionItems.push({ label: "Online/offline vendor quotations (food quotes stay committee-only)", where: "Quotes tab" });
  actionItems.push({ label: "Transportation quotations", where: "Budget tab / Quotes tab" });
  actionItems.push({ label: "Date/venue/contribution poll options", where: "Polls tab (drafts are waiting)" });
  const { message, busy, run } = useAdminAction();
  const [annTarget, setAnnTarget] = useState<Announcement | "new" | null>(null);
  const [annForm, setAnnForm] = useState({ title: "", body: "", pinned: false, published: true });
  const [faqTarget, setFaqTarget] = useState<Faq | "new" | null>(null);
  const [faqForm, setFaqForm] = useState({ question: "", answer: "", sortOrder: 0 });

  const saveAnn = () =>
    run(async () => {
      if (annTarget === "new") await api.adminSend("/announcements", "POST", annForm);
      else if (annTarget) await api.adminSend(`/announcements/${annTarget.id}`, "PUT", annForm);
      setAnnTarget(null);
      onSaved();
    }, "Announcement saved.");

  const saveFaq = () =>
    run(async () => {
      const payload = { ...faqForm, sortOrder: Number(faqForm.sortOrder) };
      if (faqTarget === "new") await api.adminSend("/faqs", "POST", payload);
      else if (faqTarget) await api.adminSend(`/faqs/${faqTarget.id}`, "PUT", payload);
      setFaqTarget(null);
      onSaved();
    }, "FAQ saved.");

  return (
    <>
      <PanelSection
        title="Announcements & FAQs"
        description="Published announcements appear on the public overview; FAQs power the questions section."
        action={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setAnnForm({ title: "", body: "", pinned: false, published: true });
                setAnnTarget("new");
              }}
            >
              <Plus className="h-4 w-4" aria-hidden="true" /> Announcement
            </Button>
            <Button
              onClick={() => {
                setFaqForm({ question: "", answer: "", sortOrder: 0 });
                setFaqTarget("new");
              }}
            >
              <Plus className="h-4 w-4" aria-hidden="true" /> FAQ
            </Button>
          </div>
        }
      >
        <h3 className="text-lg font-semibold text-white">Announcements</h3>
        <div className="mt-3 space-y-2">
          {announcements.map((a) => (
            <Card key={a.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div>
                <p className="font-medium text-white">
                  {a.title} {a.pinned && <span className="text-xs text-wandor-prompt">· pinned</span>}
                </p>
                <p className="line-clamp-2 text-sm text-wandor-muted">{a.body}</p>
              </div>
              <RowActions
                onEdit={() => {
                  setAnnForm({ title: a.title, body: a.body, pinned: a.pinned, published: true });
                  setAnnTarget(a);
                }}
                onDelete={() => {
                  if (window.confirm(`Delete announcement “${a.title}”?`)) {
                    void run(async () => {
                      await api.adminSend(`/announcements/${a.id}`, "DELETE");
                      onSaved();
                    }, "Announcement deleted.");
                  }
                }}
              />
            </Card>
          ))}
        </div>

        <h3 className="mt-8 text-lg font-semibold text-white">FAQs</h3>
        <div className="mt-3 space-y-2">
          {faqs.map((f) => (
            <Card key={f.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div>
                <p className="font-medium text-white">{f.question}</p>
                <p className="line-clamp-2 text-sm text-wandor-muted">{f.answer}</p>
              </div>
              <RowActions
                onEdit={() => {
                  setFaqForm({ question: f.question, answer: f.answer, sortOrder: f.sortOrder });
                  setFaqTarget(f);
                }}
                onDelete={() => {
                  if (window.confirm("Delete this FAQ?")) {
                    void run(async () => {
                      await api.adminSend(`/faqs/${f.id}`, "DELETE");
                      onSaved();
                    }, "FAQ deleted.");
                  }
                }}
              />
            </Card>
          ))}
        </div>

        <ActionMessage message={message} />

        {annTarget && (
          <Modal title={annTarget === "new" ? "New announcement" : "Edit announcement"} onClose={() => setAnnTarget(null)}>
            <Field label="Title" htmlFor="an-title">
              <Input id="an-title" value={annForm.title} onChange={(e) => setAnnForm((f) => ({ ...f, title: e.target.value }))} maxLength={200} />
            </Field>
            <div className="mt-4">
              <Field label="Body">
                <textarea
                  className="w-full min-h-[120px] rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2.5 text-sm outline-none focus:border-wandor-prompt"
                  value={annForm.body}
                  onChange={(e) => setAnnForm((f) => ({ ...f, body: e.target.value }))}
                  maxLength={8000}
                />
              </Field>
            </div>
            <div className="mt-4 space-y-2">
              <Checkbox label="Pin to the top" checked={annForm.pinned} onChange={(e) => setAnnForm((f) => ({ ...f, pinned: e.target.checked }))} />
              <Checkbox label="Published (visible on the public site)" checked={annForm.published} onChange={(e) => setAnnForm((f) => ({ ...f, published: e.target.checked }))} />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setAnnTarget(null)}>Cancel</Button>
              <Button onClick={saveAnn} loading={busy}>Save</Button>
            </div>
          </Modal>
        )}

        {faqTarget && (
          <Modal title={faqTarget === "new" ? "New FAQ" : "Edit FAQ"} onClose={() => setFaqTarget(null)}>
            <Field label="Question" htmlFor="fq-q">
              <Input id="fq-q" value={faqForm.question} onChange={(e) => setFaqForm((f) => ({ ...f, question: e.target.value }))} maxLength={300} />
            </Field>
            <div className="mt-4">
              <Field label="Answer">
                <textarea
                  className="w-full min-h-[120px] rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2.5 text-sm outline-none focus:border-wandor-prompt"
                  value={faqForm.answer}
                  onChange={(e) => setFaqForm((f) => ({ ...f, answer: e.target.value }))}
                  maxLength={8000}
                />
              </Field>
            </div>
            <div className="mt-4">
              <Field label="Sort order" htmlFor="fq-order">
                <Input id="fq-order" type="number" min={0} value={faqForm.sortOrder} onChange={(e) => setFaqForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))} />
              </Field>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setFaqTarget(null)}>Cancel</Button>
              <Button onClick={saveFaq} loading={busy}>Save</Button>
            </div>
          </Modal>
        )}
      </PanelSection>

      <PanelSection
        title="Data completeness — action items"
        description="Every missing figure below has NO recorded source. Nothing is fabricated on the public site — these fields show “Awaiting confirmation” until you enter the real values here."
      >
        {actionItems.length === 0 ? (
          <p className="text-sm text-white/50">All tracked data is populated.</p>
        ) : (
          <ul className="space-y-2">
            {actionItems.map((a) => (
              <li
                key={a.label}
                className="flex flex-wrap items-center justify-between gap-2 border border-amber-400/20 bg-amber-400/[0.06] px-4 py-2.5 text-sm"
              >
                <span className="text-amber-200">{a.label}</span>
                <span className="text-xs text-white/50">Enter it in: {a.where}</span>
              </li>
            ))}
          </ul>
        )}
      </PanelSection>

      <PanelSection
        title="Settlement snapshot & exports"
        description="Surplus uses actual recorded income and expenses only. CSV exports never contain secrets — no password hashes, session tokens, or voting codes."
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Card className="p-5">
            <p className="text-xs uppercase tracking-wider text-wandor-muted">Actual income</p>
            <p className="mt-1 text-xl font-semibold text-white">{formatINRExact(surplus.actualIncomePaise)}</p>
          </Card>
          <Card className="p-5">
            <p className="text-xs uppercase tracking-wider text-wandor-muted">Actual expenses</p>
            <p className="mt-1 text-xl font-semibold text-white">{formatINRExact(surplus.actualExpensePaise)}</p>
          </Card>
          <Card className={`p-5 ${surplus.isDeficit ? "border-red-400/30" : "border-emerald-400/30"}`}>
            <p className="text-xs uppercase tracking-wider text-wandor-muted">
              {surplus.isDeficit ? "Deficit" : "Surplus"}
            </p>
            <p className="mt-1 text-xl font-semibold text-white">
              {formatINRExact(Math.abs(surplus.surplusPaise))}
            </p>
            <p className="mt-1 text-xs text-wandor-muted">
              {surplus.isDeficit
                ? "No distribution calculated"
                : `${formatINRExact(surplus.perStudentPaise)} × ${surplus.eligibleCount} students (residual ${formatINRExact(surplus.residualPaise)})`}
            </p>
          </Card>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <a href="/api/admin/exports/voter-codes.csv" download>
            <Button variant="secondary" size="sm">
              <FileDown className="h-4 w-4" aria-hidden="true" /> Voter code status CSV
            </Button>
          </a>
          <a href="/api/admin/exports/votes-summary.csv" download>
            <Button variant="secondary" size="sm">
              <FileDown className="h-4 w-4" aria-hidden="true" /> Votes summary CSV
            </Button>
          </a>
          <a href="/api/admin/exports/income.csv" download>
            <Button variant="secondary" size="sm">
              <FileDown className="h-4 w-4" aria-hidden="true" /> Income CSV
            </Button>
          </a>
          <a href="/api/export/budget.csv" download>
            <Button variant="secondary" size="sm">
              <FileDown className="h-4 w-4" aria-hidden="true" /> Budget CSV
            </Button>
          </a>
        </div>

        <p className="mt-4 rounded-xl bg-white/[0.05] p-3 text-xs leading-relaxed text-wandor-muted">
          Database backup: run <code className="font-mono">npm run backup</code> on the server (see
          README “Backup & recovery”). Backups are SQLite snapshots under <code className="font-mono">backups/</code> —
          restore instructions are documented and tested.
        </p>
      </PanelSection>
    </>
  );
}
